import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, CheckCircle, ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple markdown parser for consent form content
const parseMarkdown = (content) => {
  if (!content) return '';
  
  return content
    // Headings
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-white mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-amber-400 mt-6 mb-3">$1</h2>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
    // Lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 text-slate-300">• $1</li>')
    // Paragraphs (lines that don't start with special chars)
    .replace(/^(?!<[hl]|<li)(.+)$/gm, '<p class="text-slate-400 mb-2">$1</p>')
    // Wrap consecutive list items
    .replace(/(<li.*<\/li>\n?)+/g, '<ul class="my-2">$&</ul>');
};

const ConsentModal = ({ userType, token, onConsentGiven }) => {
  const [consentForm, setConsentForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  
  const contentRef = useRef(null);

  useEffect(() => {
    fetchConsentForm();
  }, []);

  // Check if content requires scrolling and track scroll position
  const checkScrollPosition = useCallback(() => {
    const element = contentRef.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    
    // If content doesn't need scrolling (fits in viewport), enable immediately
    if (scrollHeight <= clientHeight) {
      setHasScrolledToBottom(true);
      return;
    }
    
    // Check if scrolled to bottom (with 10px buffer)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Check scroll position when content loads
  useEffect(() => {
    if (consentForm && contentRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(checkScrollPosition, 100);
    }
  }, [consentForm, checkScrollPosition]);

  // Add scroll listener
  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', checkScrollPosition);
      return () => element.removeEventListener('scroll', checkScrollPosition);
    }
  }, [checkScrollPosition, loading]);

  const fetchConsentForm = async () => {
    try {
      // Fetch the consent form specific to the user type
      const response = await axios.get(`${API}/legal/consent-form?user_type=${userType}`);
      setConsentForm(response.data.page);
    } catch (error) {
      console.error('Failed to fetch consent form:', error);
      toast.error('Failed to load consent form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitConsent = async () => {
    if (!checked || !hasScrolledToBottom) {
      if (!hasScrolledToBottom) {
        toast.error('Please scroll to the bottom to read the full consent form');
      } else {
        toast.error('Please check the consent box to proceed');
      }
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = userType === 'partner' ? '/partner/consent' : '/client/consent';
      await axios.post(
        `${API}${endpoint}`,
        { consent_given: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Consent recorded successfully');
      onConsentGiven();
    } catch (error) {
      console.error('Failed to submit consent:', error);
      toast.error('Failed to submit consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Button is only enabled when BOTH conditions are met
  const canSubmit = checked && hasScrolledToBottom;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-400">Loading consent form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-slate-900 rounded-xl border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="bg-slate-800 px-6 py-4 rounded-t-xl border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {consentForm?.title || 'Data Processing Consent'}
                </h1>
                <p className="text-sm text-slate-400">
                  Please read and accept to continue
                </p>
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="relative">
            <div 
              ref={contentRef}
              className="px-6 py-4 max-h-[60vh] overflow-y-auto"
              data-testid="consent-content"
            >
              <div 
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(consentForm?.content || '') }}
              />
            </div>
            
            {/* Scroll indicator - only show if not scrolled to bottom */}
            {!hasScrolledToBottom && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 to-transparent pt-8 pb-2 pointer-events-none">
                <div className="flex items-center justify-center gap-2 text-amber-400 animate-bounce">
                  <ChevronDown className="w-5 h-5" />
                  <span className="text-sm font-medium">Scroll to read more</span>
                  <ChevronDown className="w-5 h-5" />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-slate-800/50 px-6 py-4 rounded-b-xl border-t border-slate-700">
            {/* Links to Privacy Policy and Terms */}
            <div className="flex gap-4 mb-4 text-sm">
              <a 
                href="/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Terms of Service
              </a>
            </div>

            {/* Consent Checkbox */}
            <div className={`flex items-start gap-3 mb-4 p-3 rounded-lg border transition-all ${
              hasScrolledToBottom 
                ? 'bg-slate-900/50 border-slate-700' 
                : 'bg-slate-900/30 border-slate-700/50 opacity-60'
            }`}>
              <Checkbox
                id="consent-checkbox"
                checked={checked}
                onCheckedChange={setChecked}
                disabled={!hasScrolledToBottom}
                className="mt-0.5 border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="consent-checkbox"
              />
              <label 
                htmlFor="consent-checkbox" 
                className={`text-sm cursor-pointer select-none transition-colors ${
                  hasScrolledToBottom ? 'text-slate-300' : 'text-slate-500'
                }`}
              >
                I have read and understood the above consent form, Privacy Policy, and Terms of Service. 
                I voluntarily agree to the collection and processing of my personal data as described.
                {!hasScrolledToBottom && (
                  <span className="block mt-1 text-xs text-amber-500/80">
                    (Please scroll to the bottom to enable this checkbox)
                  </span>
                )}
              </label>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitConsent}
              disabled={!canSubmit || submitting}
              className={`w-full py-3 font-semibold transition-all ${
                canSubmit 
                  ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' 
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="consent-submit-btn"
            >
              {submitting ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  I Consent
                </>
              )}
            </Button>
            
            {/* Status hints */}
            {!canSubmit && (
              <p className="text-xs text-slate-500 text-center mt-2">
                {!hasScrolledToBottom && !checked && 'Scroll to read the full consent form, then check the box above'}
                {!hasScrolledToBottom && checked && 'Please scroll to the bottom to read the full consent form'}
                {hasScrolledToBottom && !checked && 'Please check the box above to proceed'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
